import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import useAuth from "../hooks/useAuth";

const DOCLIFT_STYLES = `
    @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .doclift-page {
      /* Brand */
      --green: #128C7E;          /* WhatsApp teal – used sparingly */
      --green-light: #25D366;    /* WhatsApp bright green – CTA only */
      --green-pale: #E8F5F1;     /* Very light tint for backgrounds */
      --green-mid: #D1EDE7;

      /* Neutrals – warm slate */
      --bg: #FFFFFF;
      --bg-soft: #F7F9F8;
      --bg-alt: #EFF3F2;
      --card: #FFFFFF;
      --border: #E2EAEB;
      --border-soft: #F0F4F4;

      /* Text */
      --text-primary: #0F2920;   /* Near-black with green tint */
      --text-secondary: #4A6A65; /* Mid-tone for body copy */
      --text-muted: #8FA8A4;     /* Labels, captions */

      /* Accents */
      --cta-bg: #25D366;
      --cta-hover: #1ebe5a;

      font-family: 'Inter', sans-serif;
      background: var(--bg);
      color: var(--text-primary);
      overflow-x: hidden;
      line-height: 1.6;
      scroll-behavior: smooth;
    }

    /* ── NAV ── */
    .doclift-page nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      padding: 0 40px;
      height: 64px;
      display: flex; align-items: center; justify-content: space-between;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(16px);
      border-bottom: 1px solid var(--border);
    }
    .doclift-page .nav-logo {
      display: flex; align-items: center; gap: 9px;
      font-size: 1.2rem; font-weight: 800; color: var(--text-primary);
      text-decoration: none; letter-spacing: -0.02em;
    }
    .doclift-page .nav-logo .logo-icon {
      width: 34px; height: 34px; background: #2563EB;
      border-radius: 9px; display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 8px rgba(37,99,235,0.25);
    }
    .doclift-page .nav-logo .logo-icon svg { width: 18px; height: 18px; fill: #fff; }
    .doclift-page .nav-cta {
      background: linear-gradient(135deg, #2563EB 0%, #3B82F6 100%);
      color: #fff;
      padding: 9px 22px; border-radius: 8px;
      font-weight: 600; font-size: 0.875rem;
      text-decoration: none; border: none; cursor: pointer;
      transition: all .25s ease;
      box-shadow: 0 2px 10px rgba(37,99,235,0.3);
    }
    .doclift-page .nav-cta:hover {
      background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
      box-shadow: 0 5px 18px rgba(37,99,235,0.4);
      transform: translateY(-1px);
    }

    /* ── HERO ── */
    .doclift-page #hero {
      position: relative;
      background: linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%);
      padding: 5rem 1.5rem;
      overflow: hidden;
      display: block;
      min-height: auto;
    }

    /* Abstract organic blue/sky blobs in the background with subtle drift animation */
    .doclift-page .blob-bg {
      position: absolute;
      z-index: 0;
      filter: blur(64px);
      opacity: 0.55;
      pointer-events: none;
      animation: doclift-blob-drift 12s ease-in-out infinite alternate;
    }
    .doclift-page .blob-bg-2 {
      animation: doclift-blob-drift 15s ease-in-out infinite alternate-reverse;
    }
    .doclift-page .blob-bg-3 {
      animation: doclift-blob-drift 18s ease-in-out 2s infinite alternate;
    }

    @keyframes doclift-blob-drift {
      0% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(18px, -14px) scale(1.08); }
      100% { transform: translate(-15px, 12px) scale(0.96); }
    }

    .doclift-page .hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.375rem 0.875rem;
      background: #E0F2FE;
      color: #0284C7;
      border-radius: 9999px;
      font-size: 0.82rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      border: 1px solid #BAE6FD;
      box-shadow: 0 2px 8px rgba(2, 132, 199, 0.12);
      letter-spacing: 0.02em;
    }

    .doclift-page .hero-badge .dot {
      width: 8px;
      height: 8px;
      background-color: #0284C7;
      border-radius: 50%;
      display: inline-block;
      box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.25);
      animation: doclift-hero-pulse 2s infinite;
    }

    @keyframes doclift-hero-pulse {
      0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.5); }
      70% { transform: scale(1); box-shadow: 0 0 0 7px rgba(2, 132, 199, 0); }
      100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
    }

    .doclift-page #hero h1 {
      text-align: left;
      margin-left: 0;
      margin-right: 0;
      max-width: 100%;
    }

    .doclift-page #hero .hero-cta-group {
      justify-content: flex-start;
    }

    /* GRADIENT CTA BUTTON WITH SHIMMER */
    .doclift-page .btn-primary {
      position: relative;
      overflow: hidden;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.625rem;
      background: linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #1D4ED8 100%);
      color: white;
      font-weight: 600;
      font-size: 1rem;
      padding: 0.875rem 1.75rem;
      border-radius: 0.75rem;
      border: none;
      box-shadow: 0 10px 24px -4px rgba(37, 99, 235, 0.42), 0 4px 8px -2px rgba(37, 99, 235, 0.25);
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      text-decoration: none;
      cursor: pointer;
    }

    .doclift-page .btn-primary::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.32), transparent);
      animation: doclift-btn-shimmer 3.5s infinite;
      pointer-events: none;
    }

    @keyframes doclift-btn-shimmer {
      0%, 65% { left: -100%; }
      100% { left: 100%; }
    }

    .doclift-page .btn-primary:hover {
      background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1E40AF 100%);
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 16px 32px -4px rgba(37, 99, 235, 0.52), 0 6px 12px -2px rgba(37, 99, 235, 0.35);
      color: white;
    }

    .doclift-page .btn-primary svg {
      width: 1.25rem;
      height: 1.25rem;
      fill: currentColor;
      filter: drop-shadow(0 1px 2px rgba(0,0,0,0.15));
    }

    /* Secondary button with refined border */
    .doclift-page .btn-secondary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.95);
      color: #374151;
      font-weight: 600;
      font-size: 1rem;
      padding: 0.875rem 1.75rem;
      border-radius: 0.75rem;
      border: 1.5px solid #E2E8F0;
      box-shadow: 0 4px 12px -1px rgba(0, 0, 0, 0.05);
      transition: all 0.25s ease;
      text-decoration: none;
      backdrop-filter: blur(8px);
    }

    .doclift-page .btn-secondary:hover {
      background-color: #F0F7FF;
      border-color: #93C5FD;
      color: #1D4ED8;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px -2px rgba(37, 99, 235, 0.15);
    }

    .doclift-page .trust-strip {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: #4b5563;
      margin-top: 2rem;
      font-weight: 500;
    }

    .doclift-page .trust-strip .check {
      color: #2563EB;
      font-weight: bold;
    }

    /* Phone mockup floating animations */
    @keyframes doclift-float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }

    @keyframes doclift-float-delayed {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-12px); }
    }

    .doclift-page .floating-phone {
      animation: doclift-float 6s ease-in-out infinite;
    }

    .doclift-page .floating-bubble-1 {
      animation: doclift-float-delayed 5s ease-in-out infinite;
    }

    .doclift-page .floating-bubble-2 {
      animation: doclift-float 7s ease-in-out infinite 1s;
    }

    /* ── FLOW + CHAT ── */
    .doclift-page #flow {
      padding: 96px 24px;
      background: linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%);
      border-top: 1px solid var(--border-soft);
      border-bottom: 1px solid var(--border-soft);
      position: relative;
    }
    .doclift-page .flow-badge {
      display: block; width: fit-content; margin: 0 auto 14px;
      background: #E0F2FE; color: #0284C7;
      font-size: 0.72rem; font-weight: 800; text-transform: uppercase;
      letter-spacing: .08em; padding: 5px 14px; border-radius: 9999px;
    }
    .doclift-page .flow-title {
      text-align: center; font-size: clamp(1.8rem, 3.8vw, 2.6rem);
      font-weight: 800; margin-bottom: 12px; letter-spacing: -0.025em;
      color: #0F172A; line-height: 1.25;
    }
    .doclift-page .flow-sparkle {
      color: #2563EB; font-size: 0.9em; vertical-align: baseline; margin-left: 6px;
    }
    .doclift-page .flow-sub {
      text-align: center; color: #475569; font-size: 1rem;
      max-width: 520px; margin: 0 auto 48px; line-height: 1.6;
    }

    /* ── ANIMATED SCENE ── */
    .doclift-page .ai-scene {
      max-width: 980px; margin: 0 auto 56px;
      background: #FFFFFF;
      border: 1.5px solid #EDF2F7;
      border-radius: 28px; padding: 38px 28px;
      box-shadow: 0 10px 40px -10px rgba(15,23,42,0.06), 0 1px 3px rgba(0,0,0,0.02);
      position: relative;
    }
    .doclift-page .scene-row {
      display: flex; align-items: center; justify-content: center;
      gap: 16px; width: 100%; max-width: 860px; margin: 0 auto;
    }
    .doclift-page .scene-actor { display: flex; flex-direction: column; align-items: center; }

    /* ---- CUSTOMER CARD ---- */
    .doclift-page .flow-customer-card {
      width: 236px; flex-shrink: 0; min-height: 230px;
      background: #FFFFFF; border-radius: 20px;
      border: 1.5px solid #F1F5F9;
      box-shadow: 0 8px 30px rgba(15,23,42,0.06);
      padding: 16px;
      display: flex; flex-direction: column;
    }
    .doclift-page .customer-card-header {
      display: flex; align-items: center; gap: 9px; margin-bottom: 12px;
      padding-bottom: 8px; border-bottom: 1px solid #F8FAFC;
    }
    .doclift-page .customer-call-btn {
      width: 30px; height: 30px; border-radius: 50%;
      background: #22C55E; display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; box-shadow: 0 2px 6px rgba(34,197,94,0.3);
    }
    .doclift-page .customer-avatar {
      width: 30px; height: 30px; border-radius: 50%;
      background: #E0F2FE; display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .doclift-page .customer-info { display: flex; flex-direction: column; }
    .doclift-page .customer-name { font-size: 0.82rem; font-weight: 700; color: #0F172A; }
    .doclift-page .customer-status { font-size: 0.68rem; color: #10B981; font-weight: 500; display: flex; align-items: center; gap: 4px; }
    
    .doclift-page .customer-chat-body {
      flex: 1; display: flex; flex-direction: column; gap: 8px;
      position: relative;
    }

    /* Customer Question Bubble - animated entry */
    .doclift-page .customer-bubble-wrap {
      background: #F1F5F9; border-radius: 14px; padding: 10px 12px;
      opacity: 0; animation: doclift-q-appear 14s infinite;
      align-self: flex-start; width: 100%;
      box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    }
    .doclift-page .bubble-text {
      font-size: 0.76rem; font-weight: 500; color: #1E293B; line-height: 1.4;
      display: flex; align-items: center; justify-content: space-between;
    }
    .doclift-page .bubble-time {
      font-size: 0.6rem; color: #94A3B8; text-align: right; margin-top: 4px;
    }

    /* Typing indicator while AI responds */
    .doclift-page .customer-typing {
      display: flex; gap: 4px; align-items: center; padding: 6px 10px;
      background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px;
      width: fit-content; opacity: 0;
      animation: doclift-typing-show 14s infinite;
      box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    }
    .doclift-page .customer-typing span {
      width: 5px; height: 5px; border-radius: 50%; background: #059669;
      animation: doclift-tdot 0.7s infinite;
    }
    .doclift-page .customer-typing span:nth-child(2){animation-delay:.15s}
    .doclift-page .customer-typing span:nth-child(3){animation-delay:.3s}

    /* AI Reply Bubble inside customer card */
    .doclift-page .customer-reply-wrap {
      background: #DCFCE7; border: 1px solid #BBF7D0;
      border-radius: 14px; padding: 10px 12px;
      opacity: 0; animation: doclift-a-appear 14s infinite;
      align-self: flex-end; width: 100%;
      box-shadow: 0 2px 6px rgba(16,185,129,0.08);
    }
    .doclift-page .customer-reply-text {
      font-size: 0.75rem; font-weight: 600; color: #0F172A; line-height: 1.4;
    }
    .doclift-page .customer-reply-meta {
      display: flex; align-items: center; justify-content: flex-end; gap: 4px;
      margin-top: 4px; font-size: 0.6rem; color: #059669; font-weight: 600;
    }

    @keyframes doclift-tdot{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
    @keyframes doclift-q-appear{
      0%,3%   {opacity:0;transform:scale(.92) translateY(6px)}
      6%,96%  {opacity:1;transform:scale(1) translateY(0)}
      99%,100%{opacity:0;transform:scale(.92) translateY(-4px)}
    }
    @keyframes doclift-typing-show{
      0%,62%  {opacity:0;transform:translateY(4px)}
      65%,76% {opacity:1;transform:translateY(0)}
      78%,100%{opacity:0;transform:translateY(-4px)}
    }
    @keyframes doclift-a-appear{
      0%,78%  {opacity:0;transform:translateY(6px)}
      81%,96% {opacity:1;transform:translateY(0)}
      99%,100%{opacity:0;transform:translateY(-4px)}
    }

    /* ---- PIPES ---- */
    .doclift-page .scene-pipe {
      flex: 1; height: 32px; position: relative; min-width: 48px; max-width: 90px;
      display: flex; align-items: center; justify-content: center;
    }
    .doclift-page .pipe-track {
      position: absolute; left: 0; right: 10px; top: 50%;
      height: 0; border-top: 2.5px dotted #93C5FD; transform: translateY(-50%);
    }
    .doclift-page .pipe-arrow {
      position: absolute; right: 0; top: 50%; transform: translateY(-50%);
      color: #3B82F6; font-size: 13px; font-weight: bold; line-height: 1;
    }
    .doclift-page .pipe-dot {
      position: absolute; top: 50%; transform: translateY(-50%);
      width: 8px; height: 8px; border-radius: 50%;
      background: #2563EB; opacity: 0; z-index: 2;
      box-shadow: 0 0 8px rgba(37,99,235,0.6);
    }
    /* Left pipe - question LTR (phone→AI) */
    .doclift-page .pipe-l>div:nth-child(3){animation:doclift-dot-ltr-q 14s infinite}
    .doclift-page .pipe-l>div:nth-child(4){animation:doclift-dot-ltr-q 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-l>div:nth-child(5){animation:doclift-dot-ltr-q 14s infinite;animation-delay:.5s}
    /* Left pipe - reply RTL (AI→phone) */
    .doclift-page .pipe-l>div:nth-child(6){animation:doclift-dot-rtl-rpl 14s infinite}
    .doclift-page .pipe-l>div:nth-child(7){animation:doclift-dot-rtl-rpl 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-l>div:nth-child(8){animation:doclift-dot-rtl-rpl 14s infinite;animation-delay:.5s}
    /* Right pipe - query LTR (AI→KB) */
    .doclift-page .pipe-r>div:nth-child(3){animation:doclift-dot-ltr-kb 14s infinite}
    .doclift-page .pipe-r>div:nth-child(4){animation:doclift-dot-ltr-kb 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-r>div:nth-child(5){animation:doclift-dot-ltr-kb 14s infinite;animation-delay:.5s}
    /* Right pipe - answer RTL (KB→AI) */
    .doclift-page .pipe-r>div:nth-child(6){animation:doclift-dot-rtl-kb 14s infinite}
    .doclift-page .pipe-r>div:nth-child(7){animation:doclift-dot-rtl-kb 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-r>div:nth-child(8){animation:doclift-dot-rtl-kb 14s infinite;animation-delay:.5s}
    @keyframes doclift-dot-ltr-q{
      0%,10%  {left:0%;  opacity:0}
      12%     {left:0%;  opacity:1}
      26%     {left:100%;opacity:1}
      28%,100%{left:100%;opacity:0}
    }
    @keyframes doclift-dot-ltr-kb{
      0%,27%  {left:0%;  opacity:0}
      29%     {left:0%;  opacity:1}
      42%     {left:100%;opacity:1}
      44%,100%{left:100%;opacity:0}
    }
    @keyframes doclift-dot-rtl-kb{
      0%,43%  {left:100%;opacity:0}
      45%     {left:100%;opacity:1}
      59%     {left:0%;  opacity:1}
      61%,100%{left:0%;  opacity:0}
    }
    @keyframes doclift-dot-rtl-rpl{
      0%,60%  {left:100%;opacity:0}
      62%     {left:100%;opacity:1}
      75%     {left:0%;  opacity:1}
      77%,100%{left:0%;  opacity:0}
    }

    /* ---- AI CENTER ---- */
    .doclift-page .flow-mascot-card {
      width: 96px; height: 96px; border-radius: 26px;
      background: #FFFFFF; display: flex; align-items: center; justify-content: center;
      box-shadow: 0 10px 30px rgba(37,99,235,0.12);
      border: 1.5px solid #EFF6FF;
      animation: doclift-ai-glow 14s infinite;
      position: relative;
    }
    @keyframes doclift-ai-glow{
      0%,26%  {box-shadow: 0 10px 30px rgba(37,99,235,0.1); border-color: #EFF6FF;}
      29%,62% {box-shadow: 0 0 0 6px rgba(37,99,235,0.12), 0 16px 36px rgba(37,99,235,0.24); border-color: #3B82F6;}
      65%,100%{box-shadow: 0 10px 30px rgba(37,99,235,0.1); border-color: #EFF6FF;}
    }
    .doclift-page .flow-ai-name {
      font-size: 0.88rem; font-weight: 800; color: #0F172A; margin-top: 10px; text-align: center;
    }
    .doclift-page .flow-ai-status {
      font-size: 0.72rem; color: #64748B; text-align: center; margin-top: 4px; font-weight: 500;
      animation: doclift-status-pulse 3s ease-in-out infinite;
    }
    @keyframes doclift-status-pulse {
      0%, 100% { opacity: 0.85; }
      50% { opacity: 1; color: #2563EB; }
    }

    /* ---- KNOWLEDGE BASE PANEL ---- */
    .doclift-page .flow-kb-card {
      width: 240px; flex-shrink: 0;
      background: #FFFFFF; border-radius: 22px;
      border: 1.5px solid #F1F5F9;
      box-shadow: 0 8px 30px rgba(15,23,42,0.06);
      padding: 16px;
      animation: doclift-kb-panel-glow 14s infinite;
    }
    @keyframes doclift-kb-panel-glow{
      0%,41%  {box-shadow: 0 8px 30px rgba(15,23,42,0.06); border-color: #F1F5F9;}
      44%,61% {box-shadow: 0 0 0 5px rgba(37,99,235,0.08), 0 14px 34px rgba(37,99,235,0.18); border-color: #93C5FD;}
      64%,100%{box-shadow: 0 8px 30px rgba(15,23,42,0.06); border-color: #F1F5F9;}
    }
    .doclift-page .kb-header {
      display: flex; align-items: center; gap: 9px; margin-bottom: 12px;
    }
    .doclift-page .kb-icon {
      width: 32px; height: 32px; border-radius: 9px;
      background: #EFF6FF; display: flex; align-items: center; justify-content: center;
      color: #2563EB; flex-shrink: 0;
    }
    .doclift-page .kb-title { font-size: 0.8rem; font-weight: 700; color: #0F172A; }
    .doclift-page .kb-sub { font-size: 0.65rem; color: #64748B; }
    .doclift-page .flow-kb-rows { display: flex; flex-direction: column; gap: 6px; }
    .doclift-page .flow-kb-row {
      background: #FFFFFF; border: 1px solid #F1F5F9;
      border-radius: 9px; padding: 7px 10px;
      font-size: 0.72rem; color: #334155;
      display: flex; align-items: center; justify-content: space-between;
      position: relative; overflow: hidden;
    }
    .doclift-page .flow-kb-row::after {
      content: ''; position: absolute;
      top: 0; left: -100%; bottom: 0; width: 65%;
      background: linear-gradient(90deg, transparent, rgba(37,99,235,0.22), transparent);
    }
    .doclift-page .flow-kb-row:nth-child(1)::after{animation:doclift-kb-sweep 14s infinite 0s}
    .doclift-page .flow-kb-row:nth-child(2)::after{animation:doclift-kb-sweep 14s infinite .5s}
    .doclift-page .flow-kb-row:nth-child(3)::after{animation:doclift-kb-sweep 14s infinite 1s}
    .doclift-page .flow-kb-row:nth-child(4)::after{animation:doclift-kb-sweep 14s infinite 1.5s}
    @keyframes doclift-kb-sweep{
      0%,41%  {left:-100%;opacity:0}
      43%     {left:-100%;opacity:1}
      58%     {left:150%;opacity:1}
      60%,100%{left:150%;opacity:0}
    }
    .doclift-page .flow-kb-row.kb-match { animation: doclift-kb-match-anim 14s infinite; }
    @keyframes doclift-kb-match-anim{
      0%,42%  {background:#FFFFFF;border-color:#F1F5F9;color:#334155}
      47%,58% {background:#EFF6FF;border-color:#93C5FD;color:#1D4ED8}
      63%,100%{background:#FFFFFF;border-color:#F1F5F9;color:#334155}
    }
    .doclift-page .kb-row-left { display: flex; align-items: center; gap: 7px; font-weight: 500; }
    .doclift-page .kb-row-icon { font-size: 0.75rem; }
    .doclift-page .kb-row-arrow { color: #94A3B8; font-size: 0.85rem; font-weight: 600; }
    .doclift-page .flow-kb-badge {
      margin-top: 10px; background: #ECFDF5; border: 1px solid #A7F3D0;
      color: #059669; border-radius: 9px; padding: 7px 10px;
      font-size: 0.68rem; font-weight: 600;
      display: flex; align-items: center; justify-content: center; gap: 6px;
    }

    /* ---- HANDWRITTEN ANNOTATION ---- */
    .doclift-page .flow-annotation {
      position: absolute; right: -72px; top: 40%; transform: translateY(-50%);
      display: flex; flex-direction: column; align-items: center;
      pointer-events: none; z-index: 10;
    }
    @media (max-width: 1200px) {
      .doclift-page .flow-annotation { right: -36px; }
    }
    @media (max-width: 1060px) {
      .doclift-page .flow-annotation { display: none; }
    }
    .doclift-page .annotation-text {
      font-family: 'Caveat', 'Segoe Print', 'Comic Sans MS', cursive, sans-serif;
      font-size: 15px; font-weight: 700; color: #2563EB;
      line-height: 1.25; transform: rotate(-6deg);
      display: flex; flex-direction: column; text-align: left;
    }
    .doclift-page .annotation-arrow {
      margin-top: 4px; transform: rotate(-5deg);
    }

    /* scene mobile */
    @media (max-width: 768px) {
      .doclift-page .scene-row { flex-direction: column; gap: 24px; }
      .doclift-page .scene-pipe { transform: rotate(90deg); margin: 12px 0; }
      .doclift-page .flow-annotation { display: none; }
    }

    /* ── EXAMPLE CONVERSATIONS GRID ── */
    .doclift-page .convos-header-row {
      display: flex; align-items: flex-end; justify-content: space-between;
      max-width: 980px; margin: 0 auto 20px; padding: 0 4px;
    }
    .doclift-page .convos-title-wrap {
      display: flex; align-items: center; gap: 6px;
    }
    .doclift-page .convos-sparkle { color: #2563EB; font-size: 1.2rem; line-height: 1; }
    .doclift-page .convos-title { font-size: 1.15rem; font-weight: 800; color: #0F172A; margin: 0; }
    .doclift-page .convos-sub { font-size: 0.82rem; color: #64748B; margin-top: 4px; }
    .doclift-page .convos-view-more {
      color: #2563EB; font-size: 0.82rem; font-weight: 600; text-decoration: none;
      display: flex; align-items: center; gap: 4px; transition: gap 0.2s ease;
    }
    .doclift-page .convos-view-more:hover { gap: 7px; color: #1D4ED8; }

    .doclift-page .convos-cards-grid {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
      max-width: 980px; margin: 0 auto;
    }
    @media (max-width: 860px) {
      .doclift-page .convos-cards-grid { grid-template-columns: 1fr; max-width: 480px; }
    }
    .doclift-page .convo-card {
      background: #FFFFFF; border-radius: 20px;
      border: 1.5px solid #F1F5F9;
      box-shadow: 0 4px 20px rgba(15,23,42,0.04);
      padding: 18px 18px 16px;
      display: flex; flex-direction: column; gap: 14px;
      transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    }
    .doclift-page .convo-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 30px rgba(37,99,235,0.09);
      border-color: #DBEAFE;
    }
    .doclift-page .convo-card-top {
      display: flex; align-items: center; justify-content: space-between;
    }
    .doclift-page .convo-user-meta {
      display: flex; align-items: center; gap: 10px;
    }
    .doclift-page .convo-avatar {
      width: 34px; height: 34px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .doclift-page .convo-avatar-blue { background: #EFF6FF; }
    .doclift-page .convo-avatar-purple { background: #FAF5FF; }
    .doclift-page .convo-avatar-green { background: #F0FDF4; }
    .doclift-page .convo-user-name { font-size: 0.82rem; font-weight: 700; color: #0F172A; }
    .doclift-page .convo-user-status { font-size: 0.68rem; color: #64748B; font-weight: 500; display: flex; align-items: center; gap: 4px; }
    .doclift-page .online-dot { color: #10B981; font-weight: bold; }
    .doclift-page .convo-tag {
      font-size: 0.68rem; font-weight: 600; padding: 4px 10px; border-radius: 9999px;
      display: flex; align-items: center; gap: 4px;
    }
    .doclift-page .tag-blue { background: #EFF6FF; color: #2563EB; }
    .doclift-page .tag-purple { background: #FAF5FF; color: #9333EA; }
    .doclift-page .tag-green { background: #F0FDF4; color: #16A34A; }

    .doclift-page .convo-chat-stack {
      display: flex; flex-direction: column; gap: 10px;
    }
    .doclift-page .convo-user-msg-row {
      display: flex; align-items: center; gap: 8px;
    }
    .doclift-page .convo-mini-avatar {
      width: 22px; height: 22px; border-radius: 50%; background: #F1F5F9;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .doclift-page .convo-user-bubble {
      flex: 1; display: flex; align-items: center; justify-content: space-between;
      font-size: 0.74rem; color: #334155; font-weight: 500;
    }
    .doclift-page .msg-time { font-size: 0.62rem; color: #94A3B8; margin-left: 6px; }

    .doclift-page .convo-ai-reply-box {
      background: #F8FAFC; border: 1px solid #F1F5F9;
      border-radius: 14px; padding: 11px 12px;
      display: flex; gap: 10px; align-items: flex-start;
    }
    .doclift-page .convo-ai-mascot { flex-shrink: 0; margin-top: 1px; }
    .doclift-page .convo-ai-content { flex: 1; }
    .doclift-page .convo-ai-content .ai-text {
      font-size: 0.73rem; line-height: 1.5; color: #1E293B; margin: 0;
    }
    .doclift-page .convo-ai-content .ai-text strong {
      font-weight: 700; color: #0F172A;
    }
    .doclift-page .ai-meta {
      display: flex; align-items: center; justify-content: flex-end; gap: 4px;
      margin-top: 5px;
    }
    .doclift-page .ai-time { font-size: 0.62rem; color: #94A3B8; }

    /* ═════════════════════════════════════════════════════════════════
       NEW SPLIT-LAYOUT SECTIONS (Benefits, Setup, Who It's For)
    ═════════════════════════════════════════════════════════════════ */
    .doclift-page .unified-split-section {
      padding: 96px 24px;
      position: relative;
      background: #FFFFFF;
      overflow: hidden;
    }
    .doclift-page .unified-split-section.bg-subtle-blue {
      background: radial-gradient(circle at 100% 0%, #F0F7FF 0%, #FFFFFF 60%, #F8FAFC 100%);
    }
    .doclift-page .unified-split-section.bg-subtle-gradient {
      background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%);
    }
    .doclift-page .unified-split-container {
      max-width: 1220px;
      margin: 0 auto;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 48px;
      position: relative;
    }

    /* Left Column */
    .doclift-page .split-left-col {
      width: 320px;
      flex-shrink: 0;
    }
    .doclift-page .split-pill-badge {
      display: inline-block;
      background: #EFF6FF;
      color: #2563EB;
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 5px 14px;
      border-radius: 9999px;
      margin-bottom: 16px;
    }
    .doclift-page .split-title {
      font-size: clamp(2rem, 3.2vw, 2.6rem);
      font-weight: 800;
      line-height: 1.15;
      color: #0F172A;
      letter-spacing: -0.025em;
      margin-bottom: 16px;
    }
    .doclift-page .split-title .highlight-blue {
      color: #2563EB;
    }
    .doclift-page .split-desc {
      font-size: 0.95rem;
      color: #64748B;
      line-height: 1.6;
      margin-bottom: 24px;
    }

    /* Left Column Extras: Check items in Benefits */
    .doclift-page .split-checks-row {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-top: 24px;
    }
    .doclift-page .split-check-item {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 0.84rem;
      font-weight: 600;
      color: #334155;
    }
    .doclift-page .split-check-circle {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #DCFCE7;
      color: #16A34A;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    /* Left Column Extras: Button in Who It's For */
    .doclift-page .split-cta-btn {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      background: #FFFFFF;
      border: 1.5px solid #2563EB;
      color: #2563EB;
      border-radius: 9999px;
      padding: 10px 22px;
      font-size: 0.88rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.25s ease;
      box-shadow: 0 2px 8px rgba(37,99,235,0.08);
      margin-top: 8px;
    }
    .doclift-page .split-cta-btn:hover {
      background: #EFF6FF;
      transform: translateX(4px);
      box-shadow: 0 4px 14px rgba(37,99,235,0.16);
    }

    /* Right Column */
    .doclift-page .split-right-col {
      flex: 1;
      min-width: 0;
      position: relative;
    }

    /* ──── SECTION 1: BENEFITS CARDS + FLOATING PHONE ──── */
    .doclift-page .benefits-combo-row {
      display: flex;
      align-items: stretch;
      gap: 16px;
      position: relative;
    }
    .doclift-page .feature-cards-trio {
      display: flex;
      flex: 1;
      gap: 16px;
    }
    .doclift-page .feature-box-card {
      flex: 1;
      background: #FFFFFF;
      border: 1.5px solid #F1F5F9;
      border-radius: 20px;
      padding: 24px 20px;
      display: flex;
      flex-direction: column;
      position: relative;
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      box-shadow: 0 4px 20px rgba(15,23,42,0.03);
    }
    .doclift-page .feature-box-card:hover {
      transform: translateY(-6px);
      border-color: #BFDBFE;
      box-shadow: 0 16px 36px rgba(37,99,235,0.08);
    }
    .doclift-page .feature-icon-badge {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 18px;
    }
    .doclift-page .badge-blue { background: #EFF6FF; }
    .doclift-page .badge-green { background: #ECFDF5; }
    .doclift-page .badge-purple { background: #EEF2FF; }

    .doclift-page .feature-card-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: #0F172A;
      margin-bottom: 8px;
      line-height: 1.3;
    }
    .doclift-page .feature-card-desc {
      font-size: 0.84rem;
      color: #64748B;
      line-height: 1.55;
      flex: 1;
    }
    .doclift-page .feature-learn-btn {
      margin-top: 20px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #2563EB;
      font-size: 0.8rem;
      font-weight: 600;
      text-decoration: none;
    }
    .doclift-page .learn-arrow-circle {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #EFF6FF;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.25s ease, background 0.25s ease;
      color: #2563EB;
    }
    .doclift-page .feature-box-card:hover .learn-arrow-circle {
      transform: translateX(4px);
      background: #DBEAFE;
    }
    .doclift-page .feature-ai-pill {
      position: absolute;
      top: 18px;
      right: 18px;
      background: #ECFDF5;
      color: #059669;
      font-size: 0.62rem;
      font-weight: 700;
      padding: 3px 9px;
      border-radius: 9999px;
      border: 1px solid #A7F3D0;
    }

    /* Floating Phone Mockup & Doodles */
    .doclift-page .floating-mockup-wrapper {
      width: 216px;
      flex-shrink: 0;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .doclift-page .floating-phone-aura {
      position: absolute;
      inset: -15px;
      background: radial-gradient(circle, rgba(147,197,253,0.35) 0%, rgba(219,234,254,0.1) 60%, transparent 80%);
      border-radius: 50%;
      filter: blur(14px);
      pointer-events: none;
      animation: doclift-aura-pulse 4s ease-in-out infinite alternate;
    }
    @keyframes doclift-aura-pulse {
      0% { opacity: 0.6; transform: scale(0.95); }
      100% { opacity: 1; transform: scale(1.05); }
    }
    .doclift-page .floating-phone-card {
      width: 100%;
      background: #FFFFFF;
      border-radius: 20px;
      border: 1.5px solid #F1F5F9;
      box-shadow: 0 20px 40px -10px rgba(37,99,235,0.14), 0 0 0 1px rgba(0,0,0,0.03);
      padding: 16px;
      transform: rotate(3deg);
      animation: doclift-phone-float 6s ease-in-out infinite alternate;
      position: relative;
      z-index: 2;
    }
    @keyframes doclift-phone-float {
      0% { transform: rotate(3deg) translateY(0px); }
      100% { transform: rotate(1deg) translateY(-8px); }
    }
    .doclift-page .phone-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 14px;
    }
    .doclift-page .phone-avatar {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: #EFF6FF;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .doclift-page .phone-bot-name {
      font-size: 0.78rem;
      font-weight: 700;
      color: #0F172A;
      line-height: 1.1;
    }
    .doclift-page .phone-bot-status {
      font-size: 0.64rem;
      color: #10B981;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 3px;
    }
    .doclift-page .phone-msg-bubble {
      background: #F1F5F9;
      border-radius: 12px;
      padding: 8px 10px;
      font-size: 0.72rem;
      color: #1E293B;
      line-height: 1.4;
      margin-bottom: 8px;
      width: fit-content;
    }
    .doclift-page .phone-typing-bubble {
      background: #F1F5F9;
      border-radius: 10px;
      padding: 6px 10px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 14px;
      width: fit-content;
    }
    .doclift-page .phone-typing-bubble span {
      width: 4.5px;
      height: 4.5px;
      border-radius: 50%;
      background: #64748B;
      animation: doclift-tdot 0.7s infinite;
    }
    .doclift-page .phone-typing-bubble span:nth-child(2) { animation-delay: .15s; }
    .doclift-page .phone-typing-bubble span:nth-child(3) { animation-delay: .3s; }

    .doclift-page .phone-input-bar {
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 9999px;
      padding: 4px 6px 4px 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .doclift-page .phone-input-placeholder {
      font-size: 0.68rem;
      color: #94A3B8;
    }
    .doclift-page .phone-send-btn {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #2563EB;
      color: #FFFFFF;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(37,99,235,0.4);
    }

    /* Hand-drawn Doodles */
    .doclift-page .doodle-item {
      position: absolute;
      z-index: 4;
      pointer-events: none;
      animation: doclift-doodle-bob 4s ease-in-out infinite alternate;
    }
    @keyframes doclift-doodle-bob {
      0% { transform: translateY(0px) rotate(0deg); }
      100% { transform: translateY(-4px) rotate(1.5deg); }
    }
    .doclift-page .doodle-text {
      font-family: 'Caveat', cursive, sans-serif;
      font-size: 1.05rem;
      font-weight: 700;
      color: #2563EB;
      line-height: 1.1;
      white-space: nowrap;
    }
    .doclift-page .doodle-benefits-top {
      top: -38px;
      right: 6px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
    .doclift-page .doodle-benefits-bottom {
      bottom: -32px;
      left: -28px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    /* ──── SECTION 2: SETUP 3-STEP TIMELINE ──── */
    .doclift-page .setup-timeline-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      padding: 10px 48px 10px 0;
    }
    .doclift-page .setup-step-box {
      flex: 1;
      max-width: 210px;
      text-align: center;
      position: relative;
    }
    .doclift-page .setup-step-icon-outer {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      margin: 0 auto 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      transition: all 0.3s ease;
      box-shadow: 0 4px 16px rgba(0,0,0,0.04);
    }
    .doclift-page .setup-step-box:hover .setup-step-icon-outer {
      transform: scale(1.08);
      box-shadow: 0 10px 24px rgba(37,99,235,0.12);
    }
    .doclift-page .step-icon-green { background: #DCFCE7; }
    .doclift-page .step-icon-purple { background: #F3E8FF; }
    .doclift-page .step-icon-blue { background: #E0F2FE; }

    .doclift-page .setup-step-num-badge {
      position: absolute;
      bottom: -2px;
      right: -2px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #2563EB;
      color: #FFFFFF;
      font-size: 0.72rem;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2.5px solid #FFFFFF;
      box-shadow: 0 2px 6px rgba(37,99,235,0.3);
    }
    .doclift-page .setup-step-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: #0F172A;
      margin-bottom: 6px;
    }
    .doclift-page .setup-step-desc {
      font-size: 0.84rem;
      color: #64748B;
      line-height: 1.5;
    }

    /* Setup Arrow Connectors */
    .doclift-page .setup-connector-line {
      flex: 1;
      height: 2px;
      position: relative;
      margin: -36px 14px 0;
      background: #E2E8F0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
    .doclift-page .setup-connector-pulse {
      position: absolute;
      top: -3px;
      left: 0;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #3B82F6;
      box-shadow: 0 0 8px #3B82F6;
      animation: doclift-step-pulse 2.8s infinite ease-in-out;
    }
    .doclift-page .delay-pulse {
      animation-delay: 1.4s;
    }
    @keyframes doclift-step-pulse {
      0% { left: 0%; opacity: 0; }
      15% { opacity: 1; }
      85% { opacity: 1; }
      100% { left: 100%; opacity: 0; }
    }
    .doclift-page .setup-connector-arrowhead {
      margin-right: -4px;
      color: #93C5FD;
    }
    .doclift-page .doodle-setup-right {
      position: absolute;
      top: -12px;
      right: -24px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    /* ──── SECTION 3: WHO IT'S FOR INDUSTRY GRID ──── */
    .doclift-page .industry-grid-wrapper {
      position: relative;
      padding-right: 56px;
    }
    .doclift-page .industries-cards-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }
    .doclift-page .industry-pill-card {
      background: #FFFFFF;
      border: 1.5px solid #F1F5F9;
      border-radius: 16px;
      padding: 12px 14px;
      display: flex;
      align-items: center;
      gap: 10px;
      transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
      box-shadow: 0 2px 8px rgba(15,23,42,0.02);
      cursor: default;
    }
    .doclift-page .industry-pill-card:hover {
      transform: translateY(-2px);
      border-color: #BFDBFE;
      box-shadow: 0 8px 20px rgba(37,99,235,0.07);
    }
    .doclift-page .industry-icon-box {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: transform 0.25s ease;
    }
    .doclift-page .industry-pill-card:hover .industry-icon-box {
      transform: scale(1.1);
    }
    .doclift-page .industry-name {
      font-size: 0.85rem;
      font-weight: 600;
      color: #1E293B;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .doclift-page .industry-chevron {
      color: #94A3B8;
      transition: transform 0.25s ease, color 0.25s ease;
      display: flex;
      align-items: center;
    }
    .doclift-page .industry-pill-card:hover .industry-chevron {
      transform: translateX(3px);
      color: #2563EB;
    }
    .doclift-page .doodle-industry-right {
      position: absolute;
      top: 12px;
      right: -28px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    /* Responsive */
    @media (max-width: 1080px) {
      .doclift-page .unified-split-container {
        flex-direction: column;
        gap: 36px;
      }
      .doclift-page .split-left-col {
        width: 100%;
      }
      .doclift-page .benefits-combo-row {
        flex-direction: column;
      }
      .doclift-page .floating-mockup-wrapper {
        width: 100%;
        max-width: 280px;
        margin: 20px auto 0;
      }
      .doclift-page .industry-grid-wrapper {
        padding-right: 0;
      }
      .doclift-page .setup-timeline-row {
        padding-right: 0;
      }
    }
    @media (max-width: 768px) {
      .doclift-page .feature-cards-trio {
        flex-direction: column;
      }
      .doclift-page .setup-timeline-row {
        flex-direction: column;
        gap: 28px;
      }
      .doclift-page .setup-connector-line {
        display: none;
      }
      .doclift-page .industries-cards-grid {
        grid-template-columns: repeat(2, 1fr);
      }
      .doclift-page .doodle-item {
        display: none;
      }
    }
    @media (max-width: 480px) {
      .doclift-page .industries-cards-grid {
        grid-template-columns: 1fr;
      }
    }

    /* ═════════════════════════════════════════════════════════════════
       AI POSITIONING / VALUE SECTION (Your business. Your knowledge. Your AI.)
    ═════════════════════════════════════════════════════════════════ */
    .doclift-page #ai-section {
      padding: 96px 24px;
      position: relative;
      background: radial-gradient(circle at 50% 50%, #F0FDF4 0%, #FFFFFF 70%, #F8FAFC 100%);
      overflow: hidden;
    }
    .doclift-page .ai-section-container {
      max-width: 1220px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 36px;
      position: relative;
    }
    .doclift-page .ai-left-col {
      width: 330px;
      flex-shrink: 0;
    }
    .doclift-page .ai-left-col .split-title {
      font-size: clamp(2rem, 3.2vw, 2.5rem);
      font-weight: 800;
      color: #0F172A;
      line-height: 1.18;
      letter-spacing: -0.025em;
      margin-bottom: 16px;
    }
    .doclift-page .ai-left-col .split-desc {
      font-size: 0.92rem;
      color: #64748B;
      line-height: 1.6;
      margin-bottom: 24px;
    }
    .doclift-page .ai-checklist-col {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .doclift-page .ai-check-item {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 0.88rem;
      font-weight: 600;
      color: #1E293B;
    }
    .doclift-page .ai-check-circle {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #059669;
      color: #FFFFFF;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      box-shadow: 0 2px 6px rgba(5,150,105,0.25);
    }

    /* Center Graphic: Floating Phone + Document Sheet + Purple Robot */
    .doclift-page .ai-center-graphic {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 380px;
    }
    .doclift-page .ai-center-blob {
      position: absolute;
      width: 320px;
      height: 320px;
      background: radial-gradient(circle, rgba(167,243,208,0.45) 0%, rgba(186,230,253,0.3) 50%, transparent 70%);
      border-radius: 50%;
      filter: blur(20px);
      pointer-events: none;
      animation: doclift-aura-pulse 5s ease-in-out infinite alternate;
    }
    /* Document sheet behind phone */
    .doclift-page .ai-bg-doc-sheet {
      position: absolute;
      width: 190px;
      height: 230px;
      background: #FFFFFF;
      border: 1.5px solid #E2E8F0;
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(15,23,42,0.06);
      transform: rotate(8deg) translate(30px, -10px);
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      opacity: 0.88;
      z-index: 1;
    }
    .doclift-page .doc-sheet-line {
      height: 6px;
      background: #E2E8F0;
      border-radius: 4px;
    }
    .doclift-page .doc-sheet-line.w-80 { width: 80%; }
    .doclift-page .doc-sheet-line.w-60 { width: 60%; }
    .doclift-page .doc-sheet-line.w-90 { width: 90%; }
    .doclift-page .doc-sheet-line.w-40 { width: 40%; }

    /* Floating Chat Phone */
    .doclift-page .ai-chat-mockup-card {
      width: 230px;
      background: #FFFFFF;
      border: 1.5px solid #F1F5F9;
      border-radius: 20px;
      padding: 18px 16px;
      box-shadow: 0 20px 45px -10px rgba(5,150,105,0.14), 0 0 0 1px rgba(0,0,0,0.02);
      transform: rotate(-3deg);
      position: relative;
      z-index: 2;
      animation: doclift-ai-phone-float 6s ease-in-out infinite alternate;
    }
    @keyframes doclift-ai-phone-float {
      0% { transform: rotate(-3deg) translateY(0px); }
      100% { transform: rotate(-1deg) translateY(-8px); }
    }

    /* Floating Purple Robot Face Badge */
    .doclift-page .ai-floating-purple-robot {
      position: absolute;
      top: 25px;
      right: 22%;
      z-index: 5;
      display: flex;
      flex-direction: column;
      align-items: center;
      animation: doclift-purple-bot-bob 4.5s ease-in-out infinite alternate;
    }
    @keyframes doclift-purple-bot-bob {
      0% { transform: translateY(0px) rotate(8deg); }
      100% { transform: translateY(-7px) rotate(14deg); }
    }
    .doclift-page .purple-bot-badge {
      width: 50px;
      height: 50px;
      border-radius: 16px;
      background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 10px 24px rgba(124,58,237,0.32);
      border: 2px solid #FFFFFF;
    }
    .doclift-page .purple-bot-sparks {
      display: flex;
      gap: 3px;
      margin-bottom: 4px;
    }
    .doclift-page .purple-bot-sparks span {
      width: 2px;
      height: 7px;
      background: #059669;
      border-radius: 2px;
    }
    .doclift-page .purple-bot-sparks span:nth-child(1) { transform: rotate(-25deg); height: 6px; }
    .doclift-page .purple-bot-sparks span:nth-child(2) { height: 9px; }
    .doclift-page .purple-bot-sparks span:nth-child(3) { transform: rotate(25deg); height: 6px; }

    /* Right Column: 3 Horizontal Cards Stacked */
    .doclift-page .ai-right-stack {
      width: 360px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .doclift-page .ai-stack-card {
      background: #FFFFFF;
      border: 1.5px solid #F1F5F9;
      border-radius: 18px;
      padding: 16px 18px;
      display: flex;
      align-items: center;
      gap: 14px;
      transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);
      box-shadow: 0 4px 16px rgba(15,23,42,0.03);
      cursor: pointer;
    }
    .doclift-page .ai-stack-card.active-blue {
      border-color: #3B82F6;
      box-shadow: 0 6px 20px rgba(59,130,246,0.12);
    }
    .doclift-page .ai-stack-card:hover {
      transform: translateX(4px);
      box-shadow: 0 10px 28px rgba(37,99,235,0.08);
      border-color: #93C5FD;
    }
    .doclift-page .ai-stack-icon-box {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: transform 0.25s ease;
    }
    .doclift-page .ai-stack-card:hover .ai-stack-icon-box {
      transform: scale(1.08);
    }
    .doclift-page .ai-stack-content {
      flex: 1;
      min-width: 0;
    }
    .doclift-page .ai-stack-title-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
    }
    .doclift-page .ai-stack-title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #0F172A;
    }
    .doclift-page .ai-stack-desc {
      font-size: 0.78rem;
      color: #64748B;
      line-height: 1.45;
    }
    .doclift-page .ai-stack-chevron {
      color: #94A3B8;
      transition: transform 0.25s ease, color 0.25s ease;
    }
    .doclift-page .ai-stack-card.active-blue .ai-stack-chevron,
    .doclift-page .ai-stack-card:hover .ai-stack-chevron {
      transform: translateX(3px);
      color: #2563EB;
    }

    /* ── FINAL CTA ── */
    .doclift-page #final-cta {
      padding: 100px 24px;
      background: linear-gradient(160deg, #0B4A3C 0%, #0D5C48 50%, #075E54 100%);
      text-align: center; position: relative; overflow: hidden;
    }
    .doclift-page #final-cta::before {
      content: '';
      position: absolute; inset: 0;
      background-image: radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
      background-size: 28px 28px;
      pointer-events: none;
    }
    .doclift-page #final-cta h2 {
      font-size: clamp(1.8rem, 4vw, 2.8rem);
      font-weight: 800; letter-spacing: -0.025em;
      max-width: 580px; margin: 0 auto 14px;
      color: #fff; position: relative;
    }
    .doclift-page #final-cta p { color: rgba(255,255,255,0.7); margin-bottom: 36px; font-size: 1rem; position: relative; }
    .doclift-page #final-cta .btn-cta-white {
      display: inline-flex; align-items: center; gap: 9px;
      background: #fff; color: var(--green);
      padding: 14px 32px; border-radius: 8px;
      font-weight: 700; font-size: 1rem;
      text-decoration: none;
      transition: all .25s ease;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      position: relative;
    }
    .doclift-page #final-cta .btn-cta-white:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
    .doclift-page #final-cta .btn-cta-white svg { width: 18px; height: 18px; fill: var(--green); }

    /* ── FOOTER ── */
    .doclift-page footer {
      padding: 28px 40px;
      background: #063830;
      border-top: 1px solid rgba(255,255,255,0.06);
      display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
    }
    .doclift-page footer .brand { font-weight: 700; color: rgba(255,255,255,0.5); font-size: 0.875rem; }
    .doclift-page footer .tagline { font-size: 0.78rem; color: rgba(255,255,255,0.3); }

    /* ── ANIMATIONS ── */
    .doclift-page .fade-up {
      opacity: 0; transform: translateY(28px);
      transition: opacity .55s ease, transform .55s ease;
    }
    .doclift-page .fade-up.visible { opacity: 1; transform: translateY(0); }
    .doclift-page .delay-1 { transition-delay: .08s; }
    .doclift-page .delay-2 { transition-delay: .16s; }
    .doclift-page .delay-3 { transition-delay: .24s; }
    .doclift-page .delay-4 { transition-delay: .32s; }

    /* ═════════════════════════════════════════════════════════════════
       PRICING SECTION
    ═════════════════════════════════════════════════════════════════ */
    .doclift-page #pricing {
      padding: 96px 24px;
      background: radial-gradient(circle at 50% 0%, #F0FDF4 0%, #FFFFFF 65%, #F8FAFC 100%);
      position: relative;
    }
    .doclift-page .pricing-header-wrap {
      text-align: center;
      max-width: 680px;
      margin: 0 auto 48px;
      position: relative;
    }
    .doclift-page .pricing-badge-green {
      background: #ECFDF5;
      color: #059669;
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 5px 14px;
      border-radius: 9999px;
      display: inline-block;
      margin-bottom: 14px;
    }
    .doclift-page .pricing-main-title {
      font-size: clamp(2rem, 3.5vw, 2.6rem);
      font-weight: 800;
      color: #0F172A;
      letter-spacing: -0.025em;
      margin-bottom: 12px;
    }
    .doclift-page .pricing-main-sub {
      font-size: 0.98rem;
      color: #64748B;
    }

    /* Toggle on the right / center */
    .doclift-page .pricing-toggle-wrap {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      max-width: 820px;
      margin: 0 auto 24px;
      position: relative;
    }
    .doclift-page .billing-toggle-pill {
      background: #FFFFFF;
      border: 1.5px solid #E2E8F0;
      border-radius: 9999px;
      padding: 4px;
      display: inline-flex;
      align-items: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    .doclift-page .billing-toggle-btn {
      padding: 6px 18px;
      border-radius: 9999px;
      font-size: 0.8rem;
      font-weight: 600;
      border: none;
      cursor: pointer;
      transition: all 0.25s ease;
      background: transparent;
      color: #64748B;
    }
    .doclift-page .billing-toggle-btn.active {
      background: #065F46;
      color: #FFFFFF;
      box-shadow: 0 2px 8px rgba(6,95,70,0.25);
    }
    .doclift-page .billing-save-badge {
      margin-top: 6px;
      margin-right: 12px;
      background: #DCFCE7;
      color: #065F46;
      font-size: 0.68rem;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 9999px;
      border: 1px solid #BBF7D0;
    }

    /* 2 Pricing Cards Grid */
    .doclift-page .pricing-cards-two {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      max-width: 820px;
      margin: 0 auto;
      align-items: stretch;
    }

    /* Left Card: Emerald First Year FREE */
    .doclift-page .price-card-emerald {
      background: linear-gradient(150deg, #064E3B 0%, #065F46 60%, #047857 100%);
      border-radius: 24px;
      padding: 36px 32px;
      color: #FFFFFF;
      position: relative;
      overflow: hidden;
      box-shadow: 0 16px 45px rgba(6,78,59,0.22);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      display: flex;
      flex-direction: column;
    }
    .doclift-page .price-card-emerald:hover {
      transform: translateY(-4px);
      box-shadow: 0 24px 50px rgba(6,78,59,0.32);
    }
    .doclift-page .emerald-offer-pill {
      background: rgba(255,255,255,0.18);
      border: 1px solid rgba(255,255,255,0.28);
      color: #FFFFFF;
      font-size: 0.68rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      padding: 4px 12px;
      border-radius: 9999px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      width: fit-content;
      margin-bottom: 20px;
    }
    .doclift-page .emerald-plan-name {
      font-size: 1.05rem;
      font-weight: 700;
      color: rgba(255,255,255,0.92);
      margin-bottom: 4px;
    }
    .doclift-page .emerald-big-amount {
      font-size: 3.4rem;
      font-weight: 900;
      letter-spacing: -0.03em;
      line-height: 1.1;
      margin-bottom: 12px;
    }
    .doclift-page .emerald-plan-note {
      font-size: 0.82rem;
      color: rgba(255,255,255,0.8);
      line-height: 1.55;
      margin-bottom: 24px;
    }
    .doclift-page .emerald-features-list {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .doclift-page .emerald-features-list li {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 0.84rem;
      color: rgba(255,255,255,0.92);
      font-weight: 500;
    }
    .doclift-page .emerald-check-circle {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: rgba(255,255,255,0.22);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #FFFFFF;
    }
    /* Sparkle stars in emerald card */
    .doclift-page .emerald-sparkle {
      position: absolute;
      color: rgba(255,255,255,0.35);
      font-size: 1.1rem;
      pointer-events: none;
      animation: doclift-twinkle 3s ease-in-out infinite alternate;
    }
    .doclift-page .sparkle-pos-1 { top: 70px; right: 40px; animation-delay: 0.3s; font-size: 1.4rem; }
    .doclift-page .sparkle-pos-2 { top: 110px; right: 80px; animation-delay: 1.1s; font-size: 0.9rem; }
    .doclift-page .sparkle-pos-3 { bottom: 60px; right: 50px; animation-delay: 0.7s; font-size: 1.2rem; }
    .doclift-page .sparkle-pos-4 { bottom: 100px; right: 85px; animation-delay: 1.8s; font-size: 0.8rem; }
    @keyframes doclift-twinkle {
      0% { opacity: 0.25; transform: scale(0.85); }
      100% { opacity: 0.85; transform: scale(1.15); }
    }

    /* Right Card: Crisp White Annual Plan */
    .doclift-page .price-card-white {
      background: #FFFFFF;
      border: 1.5px solid #F1F5F9;
      border-radius: 24px;
      padding: 36px 32px;
      box-shadow: 0 4px 24px rgba(15,23,42,0.04);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      display: flex;
      flex-direction: column;
    }
    .doclift-page .price-card-white:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 36px rgba(15,23,42,0.08);
      border-color: #E2E8F0;
    }
    .doclift-page .white-plan-badge {
      background: #ECFDF5;
      color: #059669;
      font-size: 0.68rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      padding: 4px 12px;
      border-radius: 9999px;
      width: fit-content;
      margin-bottom: 20px;
    }
    .doclift-page .white-plan-name {
      font-size: 1.05rem;
      font-weight: 700;
      color: #0F172A;
      margin-bottom: 4px;
    }
    .doclift-page .white-amount-wrap {
      display: flex;
      align-items: baseline;
      gap: 3px;
      margin-bottom: 12px;
    }
    .doclift-page .white-big-amount {
      font-size: 3.2rem;
      font-weight: 900;
      color: #0F172A;
      letter-spacing: -0.03em;
      line-height: 1.1;
    }
    .doclift-page .white-amount-period {
      font-size: 1.05rem;
      font-weight: 600;
      color: #64748B;
    }
    .doclift-page .white-plan-note {
      font-size: 0.82rem;
      color: #64748B;
      line-height: 1.55;
      margin-bottom: 24px;
    }
    .doclift-page .white-features-list {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 28px;
    }
    .doclift-page .white-features-list li {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 0.84rem;
      color: #334155;
      font-weight: 500;
    }
    .doclift-page .white-check-circle {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #DCFCE7;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #16A34A;
    }
    .doclift-page .white-card-cta-btn {
      margin-top: auto;
      background: #065F46;
      color: #FFFFFF;
      font-size: 0.92rem;
      font-weight: 700;
      border-radius: 12px;
      padding: 13px;
      text-decoration: none;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: all 0.25s ease;
      box-shadow: 0 4px 14px rgba(6,95,70,0.22);
    }
    .doclift-page .white-card-cta-btn:hover {
      background: #047857;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(6,95,70,0.3);
    }
    .doclift-page .white-card-cta-btn svg {
      transition: transform 0.2s ease;
    }
    .doclift-page .white-card-cta-btn:hover svg {
      transform: translateX(4px);
    }

    /* Zero Fees Row Below */
    .doclift-page .pricing-bottom-bar-wrap {
      max-width: 820px;
      margin: 40px auto 0;
      text-align: center;
    }
    .doclift-page .pricing-zero-fees-label {
      font-size: 0.78rem;
      font-weight: 700;
      color: #065F46;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 14px;
    }
    .doclift-page .pricing-zero-fees-pill-bar {
      background: #FFFFFF;
      border: 1.5px solid #F1F5F9;
      border-radius: 9999px;
      box-shadow: 0 4px 20px rgba(15,23,42,0.03);
      padding: 10px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
    .doclift-page .zero-fee-badge-item {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 0.78rem;
      font-weight: 600;
      color: #334155;
    }
    .doclift-page .zero-fee-icon-circle {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #ECFDF5;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.8rem;
      flex-shrink: 0;
    }

    /* Responsive for ai-section and pricing */
    @media (max-width: 1080px) {
      .doclift-page .ai-section-container {
        flex-direction: column;
        gap: 40px;
      }
      .doclift-page .ai-left-col,
      .doclift-page .ai-right-stack {
        width: 100%;
      }
      .doclift-page .pricing-cards-two {
        grid-template-columns: 1fr;
      }
      .doclift-page .pricing-zero-fees-pill-bar {
        border-radius: 20px;
        flex-wrap: wrap;
        justify-content: center;
        gap: 16px;
      }
    }
    @media (max-width: 640px) {
      .doclift-page .pricing-zero-fees-pill-bar {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    /* ── RESPONSIVE ── */
    @media (max-width: 768px) {
      .doclift-page .pricing-grid { grid-template-columns: 1fr; }
      .doclift-page nav { padding: 0 20px; }
      .doclift-page .benefits-grid { grid-template-columns: 1fr; }
      .doclift-page .chat-demo-grid { grid-template-columns: 1fr; }
      .doclift-page .steps-row { flex-direction: column; gap: 36px; }
      .doclift-page .steps-row::before { display: none; }
      .doclift-page .ai-card { padding: 36px 24px; }
      .doclift-page footer { flex-direction: column; text-align: center; }
      .doclift-page .flow-arrow { display: none; }
    }
    @media (max-width: 480px) {
      .doclift-page .hero-cta-group { flex-direction: column; align-items: center; }
      .doclift-page .price-amount { font-size: 2.8rem; }
    }
`;

const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        <path d="M12.004 2.003C6.486 2.003 2 6.49 2 12.008c0 1.757.456 3.468 1.325 4.978L2 22l5.156-1.303A9.954 9.954 0 0012.004 22c5.518 0 10.004-4.487 10.004-10.005C22.004 6.483 17.52 2 12.004 2zm-.002 18.207a8.285 8.285 0 01-4.217-1.152l-.303-.179-3.062.774.808-2.979-.197-.307A8.205 8.205 0 013.797 12c0-4.553 3.654-8.207 8.207-8.207 4.551 0 8.205 3.654 8.203 8.207 0 4.551-3.654 8.207-8.205 8.207z" />
    </svg>
);

const DocLiftRobotMascot = ({ size = 72, className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={{ overflow: "visible", display: "inline-block" }}
    >
        <defs>
            <linearGradient id="docliftMascotGrad" x1="15" y1="12" x2="85" y2="88" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#38BDF8" />
                <stop offset="45%" stopColor="#2563EB" />
                <stop offset="100%" stopColor="#1D4ED8" />
            </linearGradient>
            <filter id="docliftMascotShadow" x="-10%" y="-10%" width="130%" height="130%">
                <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="#1D4ED8" floodOpacity="0.24" />
            </filter>
        </defs>

        {/* Main Document Body with top-right fold */}
        <path
            d="M24 14 C17 14 12 19 12 26 L12 74 C12 81 17 86 24 86 L64 86 C71 86 76 81 76 74 L76 38 L54 14 Z"
            fill="url(#docliftMascotGrad)"
            filter="url(#docliftMascotShadow)"
        />

        {/* Folded Flap in top right corner */}
        <path
            d="M54 14 L76 38 L57 38 C55.3 38 54 36.7 54 35 Z"
            fill="#93C5FD"
        />
        <path
            d="M54 14 L76 38"
            stroke="#1E40AF"
            strokeWidth="1.5"
            strokeOpacity="0.25"
        />

        {/* Mascot Face: Friendly Eyes */}
        <circle cx="34" cy="49" r="3.8" fill="#FFFFFF" />
        <circle cx="54" cy="49" r="3.8" fill="#FFFFFF" />

        {/* Mascot Face: Happy Smile */}
        <path
            d="M39 59 C42 63.5 46 63.5 49 59"
            stroke="#FFFFFF"
            strokeWidth="2.8"
            strokeLinecap="round"
            fill="none"
        />

        {/* Speech Bubble Badge at Bottom Right */}
        <g transform="translate(54, 53)">
            <rect x="0" y="0" width="34" height="26" rx="8" fill="#1E40AF" stroke="#FFFFFF" strokeWidth="2.2" />
            {/* Speech bubble tail pointing to mascot */}
            <path d="M4 22 L-3 26 L4 16 Z" fill="#1E40AF" />
            {/* Three white dots in speech bubble */}
            <circle cx="9" cy="13" r="2.2" fill="#FFFFFF" />
            <circle cx="17" cy="13" r="2.2" fill="#FFFFFF" />
            <circle cx="25" cy="13" r="2.2" fill="#FFFFFF" />
        </g>
    </svg>
);

const DoubleCheckIcon = () => (
    <svg width="15" height="11" viewBox="0 0 16 11" fill="none" style={{ display: "inline-block", verticalAlign: "middle" }}>
        <path d="M11.05 1L4.85 7.2L3.05 5.4" stroke="#38BDF8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.05 1L8.85 7.2L7.95 6.3" stroke="#38BDF8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChatBubbleIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <line x1="8" y1="9" x2="16" y2="9" />
        <line x1="8" y1="13" x2="13" y2="13" />
    </svg>
);

const BotHeadIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="10" rx="3" />
        <circle cx="8.5" cy="16" r="1.5" fill="#059669" />
        <circle cx="15.5" cy="16" r="1.5" fill="#059669" />
        <path d="M12 2v4" />
        <circle cx="12" cy="2" r="1.5" fill="#059669" />
        <path d="M2 15h1" />
        <path d="M21 15h1" />
    </svg>
);

const ChartTrendIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
    </svg>
);

const DocEditIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
    </svg>
);

const ChatTripleIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0284C7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
);

const ShoppingBagIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
);

const ClinicCrossIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#0D9488" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 6v12" />
        <path d="M6 12h12" />
    </svg>
);

const ScissorsIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <line x1="20" y1="4" x2="8.12" y2="15.88" />
        <line x1="14.47" y1="14.48" x2="20" y2="20" />
        <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
);

const UtensilsIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2v20" />
        <path d="M21 2c0 2.5-1.5 5-3 5" />
        <path d="M6 2v7a3 3 0 0 0 6 0V2" />
        <path d="M9 9v13" />
    </svg>
);

const CogIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const UserProfileIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const DeliveryTruckIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13" rx="1" />
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
);

const LotusFlowerIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#DB2777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3c-2 4-5 7-9 8 3 3 7 3 9-1 2 4 6 4 9 1-4-1-7-4-9-8z" />
        <path d="M12 12c-1.5 3-4 5.5-7 6 2 2 5 2 7 0 2 2 5 2 7 0-3-.5-5.5-3-7-6z" />
    </svg>
);

const GraduationCapIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
);

const HardHatIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 18h20" />
        <path d="M4 18v-4a8 8 0 0 1 16 0v4" />
        <path d="M10 6v4" />
        <path d="M14 6v4" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const HomeComponent2 = () => {
    const navRef = useRef(null);
    const rootRef = useRef(null);
    const { user: userInfo } = useAuth();

    useEffect(() => {
        document.title = "DocLift – Turn WhatsApp into Your Business Assistant";

        const root = rootRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        root?.querySelectorAll(".fade-up").forEach((el) => {
            if (!el.classList.contains("visible")) observer.observe(el);
        });

        const handleScroll = () => {
            if (navRef.current) {
                navRef.current.style.boxShadow =
                    window.scrollY > 40 ? "0 2px 16px rgba(0,0,0,0.07)" : "none";
            }
        };
        window.addEventListener("scroll", handleScroll);

        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <div className="doclift-page" ref={rootRef}>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link
                href="https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&family=Inter:wght@300;400;500;600;700;800;900&display=swap"
                rel="stylesheet"
            />
            <style>{DOCLIFT_STYLES}</style>

            {/* NAV */}
            {userInfo ? (
                <Header />
            ) : (
                <nav ref={navRef}>
                    <a className="nav-logo" href="#hero" id="nav-logo">
                        <div className="logo-icon">
                            <WhatsAppIcon />
                        </div>
                        DocLift
                    </a>
                    <Link to="/dashboard" className="nav-cta" id="nav-get-started">
                        Get Started
                    </Link>
                </nav>
            )}

            {/* HERO */}
            <section id="hero" className="relative overflow-hidden pt-12 pb-20 lg:py-24">
                {/* Background Organic Blue/Sky Blob Shapes with Drift Animation */}
                <div className="blob-bg w-96 h-96 rounded-full bg-sky-200/60 top-10 right-0 -mr-20"></div>
                <div className="blob-bg blob-bg-2 w-80 h-80 rounded-full bg-blue-200/50 bottom-0 left-1/3"></div>
                <div className="blob-bg blob-bg-3 w-72 h-72 rounded-full bg-indigo-100/50 -bottom-10 right-1/4"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">

                        {/* Left Column: Headline, Copy & CTAs */}
                        <div className="lg:col-span-7 text-left">
                            <div className="hero-badge fade-up visible">
                                <span className="dot"></span> WhatsApp Business AI
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.1] mb-6 fade-up visible delay-1 text-left">
                                Turn WhatsApp into your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-sky-500">business assistant.</span>
                            </h1>

                            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mb-8 leading-relaxed fade-up visible delay-2 text-left">
                                Connect your WhatsApp. Chat with customers. Let AI handle the routine questions.
                            </p>

                            <div className="hero-cta-group flex flex-col sm:flex-row items-stretch sm:items-center gap-4 fade-up visible delay-3 justify-start">
                                <Link to="/dashboard" className="btn-primary" id="hero-get-started">
                                    <WhatsAppIcon />
                                    <span>Get Started — It's Free</span>
                                </Link>
                                <a href="#flow" className="btn-secondary" id="hero-see-how">See how it works</a>
                            </div>

                            <div className="trust-strip fade-up visible delay-4">
                                <span className="check">&#10003;</span> No credit card needed &nbsp;&bull;&nbsp;&nbsp;
                                <span className="check">&#10003;</span> Setup in 5 minutes &nbsp;&bull;&nbsp;&nbsp;
                                <span className="check">&#10003;</span> Works on your existing number
                            </div>
                        </div>

                        {/* Right Column: Upright Phone with Floating Badges */}
                        <div className="lg:col-span-5 relative flex justify-center items-center mt-8 lg:mt-0">

                            {/* Container for Phone and Floating Elements */}
                            <div className="relative w-full max-w-[360px] sm:max-w-[400px] h-[520px] sm:h-[560px] flex items-center justify-center">

                                {/* Floating WhatsApp Icon Badge (Left) */}
                                <div className="floating-bubble-1 absolute -left-4 sm:-left-8 top-12 z-30 bg-white p-3.5 rounded-2xl shadow-xl border border-emerald-50 flex items-center justify-center">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-md">
                                        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Floating Notification 1 (Top Right) */}
                                <div className="floating-bubble-2 absolute -right-2 sm:-right-6 top-16 z-30 bg-white px-4 py-3 rounded-2xl shadow-xl border border-gray-100 flex items-center space-x-3 max-w-[210px]">
                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-xs flex-shrink-0">🤖</div>
                                    <div className="text-xs">
                                        <p className="font-semibold text-gray-800">Instant AI reply</p>
                                        <p className="text-gray-500 text-[10px]">Order confirmed!</p>
                                    </div>
                                </div>

                                {/* Floating Notification 2 (Middle Right) */}
                                <div className="floating-bubble-1 absolute -right-4 sm:-right-8 top-44 z-30 bg-white px-4 py-3 rounded-2xl shadow-xl border border-gray-100 flex items-center space-x-3 max-w-[210px]">
                                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">&#10003;</div>
                                    <div className="text-xs">
                                        <p className="font-semibold text-gray-800">24/7 Bot Active</p>
                                        <p className="text-gray-500 text-[10px]">Answering FAQs</p>
                                    </div>
                                </div>

                                {/* Floating Notification 3 (Bottom Right) */}
                                <div className="floating-bubble-2 absolute -right-2 sm:-right-6 bottom-16 z-30 bg-white px-4 py-3 rounded-2xl shadow-xl border border-gray-100 flex items-center space-x-3 max-w-[210px]">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs flex-shrink-0">💬</div>
                                    <div className="text-xs">
                                        <p className="font-semibold text-gray-800">New lead captured</p>
                                        <p className="text-gray-500 text-[10px]">Sent to dashboard</p>
                                    </div>
                                </div>

                                {/* Smartphone Mockup Frame */}
                                <div className="floating-phone w-[280px] sm:w-[310px] h-[500px] sm:h-[530px] bg-gray-900 rounded-[45px] p-3.5 shadow-2xl border-4 border-gray-800 relative z-20">
                                    {/* Phone Screen Inner */}
                                    <div className="w-full h-full bg-slate-50 rounded-[34px] overflow-hidden flex flex-col relative border border-gray-700">

                                        {/* Speaker Notch & Camera */}
                                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-5 bg-gray-900 rounded-b-xl z-30 flex items-center justify-center">
                                            <div className="w-12 h-1 bg-gray-700 rounded-full"></div>
                                        </div>

                                        {/* WhatsApp Header Bar */}
                                        <div className="bg-[#075E54] pt-7 pb-3 px-4 text-white flex items-center justify-between shadow-md">
                                            <div className="flex items-center space-x-2.5">
                                                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">🤖</div>
                                                <div>
                                                    <p className="text-xs font-bold leading-tight">DocLift.ai</p>
                                                    <p className="text-[10px] text-emerald-200">Online &bull; Ready to chat</p>
                                                </div>
                                            </div>
                                            <div className="text-xs text-emerald-200 font-medium bg-emerald-900/40 px-2 py-0.5 rounded">Pro</div>
                                        </div>

                                        {/* WhatsApp Chat Body */}
                                        <div className="flex-1 p-3 overflow-y-auto space-y-3 bg-[#efeae2] text-xs">
                                            <div className="text-center my-1">
                                                <span className="bg-amber-100/80 text-amber-800 text-[9px] px-2 py-1 rounded-md shadow-sm">Today</span>
                                            </div>

                                            {/* Customer Message */}
                                            <div className="flex justify-start">
                                                <div className="bg-white p-2.5 rounded-2xl rounded-tl-sm shadow-sm max-w-[80%] text-gray-800">
                                                    <p>Hi! What are your opening hours for this weekend?</p>
                                                    <span className="text-[9px] text-gray-400 block text-right mt-1">10:14 AM</span>
                                                </div>
                                            </div>

                                            {/* AI Automated Response */}
                                            <div className="flex justify-end">
                                                <div className="bg-[#dcf8c6] p-2.5 rounded-2xl rounded-tr-sm shadow-sm max-w-[80%] text-gray-800">
                                                    <p>Hello! 👋 We are open Saturday &amp; Sunday from 9:00 AM to 8:00 PM. Would you like to book an appointment?</p>
                                                    <div className="flex items-center justify-end space-x-1 mt-1">
                                                        <span className="text-[9px] text-emerald-700 font-medium">AI Handled</span>
                                                        <span className="text-[9px] text-emerald-600">&#10003;&#10003;</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Customer Reply */}
                                            <div className="flex justify-start">
                                                <div className="bg-white p-2.5 rounded-2xl rounded-tl-sm shadow-sm max-w-[80%] text-gray-800">
                                                    <p>Yes please, book me for tomorrow at 2 PM.</p>
                                                    <span className="text-[9px] text-gray-400 block text-right mt-1">10:15 AM</span>
                                                </div>
                                            </div>

                                            {/* AI Confirmation */}
                                            <div className="flex justify-end">
                                                <div className="bg-[#dcf8c6] p-2.5 rounded-2xl rounded-tr-sm shadow-sm max-w-[80%] text-gray-800">
                                                    <p>Slot confirmed! 🎉 I've sent a calendar invite to your chat.</p>
                                                    <span className="text-[9px] text-emerald-600 block text-right mt-1">10:15 AM &#10003;&#10003;</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Chat Input Mock */}
                                        <div className="p-2 bg-white border-t border-gray-200 flex items-center space-x-2">
                                            <div className="bg-gray-100 flex-1 rounded-full px-3 py-2 text-gray-400 text-[11px]">Type a message...</div>
                                            <div className="w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center text-white text-xs">➤</div>
                                        </div>

                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* FLOW + CHAT DEMO */}
            <section id="flow">
                <p className="flow-badge fade-up">AI ASSISTANT</p>
                <h2 className="flow-title fade-up delay-1">
                    See it work in real conversations <span className="flow-sparkle">&#10022;</span>
                </h2>
                <p className="flow-sub fade-up delay-2">
                    Your customers WhatsApp you. DocLift replies &mdash; instantly, using your own business
                    information.
                </p>

                <div className="ai-scene fade-up delay-2">
                    <div className="scene-row">
                        {/* CUSTOMER CARD */}
                        <div className="scene-actor">
                            <div className="flow-customer-card">
                                <div className="customer-card-header">
                                    <div className="customer-call-btn">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFFFFF">
                                            <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                        </svg>
                                    </div>
                                    <div className="customer-avatar">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#0284C7">
                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                        </svg>
                                    </div>
                                    <div className="customer-info">
                                        <div className="customer-name">Priya S.</div>
                                        <div className="customer-status"><span className="online-dot">&#9679;</span> Online</div>
                                    </div>
                                </div>
                                <div className="customer-chat-body">
                                    {/* Question Bubble */}
                                    <div className="customer-bubble-wrap">
                                        <div className="bubble-text">
                                            <span>What are your timings?</span>
                                            <span>&#128336;</span>
                                        </div>
                                        <div className="bubble-time">11:24 AM</div>
                                    </div>

                                    {/* Typing Indicator while AI processes */}
                                    <div className="customer-typing">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    {/* AI Reply Bubble */}
                                    <div className="customer-reply-wrap">
                                        <div className="customer-reply-text">
                                            We're open 10 AM &ndash; 8 PM, Mon&ndash;Sat &#9989;
                                        </div>
                                        <div className="customer-reply-meta">
                                            <span>11:25 AM</span>
                                            <DoubleCheckIcon />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* LEFT PIPE */}
                        <div className="scene-pipe pipe-l">
                            <div className="pipe-track"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                        </div>

                        {/* AI BOT CENTER */}
                        <div className="scene-actor">
                            <div className="flow-mascot-card">
                                <DocLiftRobotMascot size={74} />
                            </div>
                            <div className="flow-ai-name">DocLift AI</div>
                            <div className="flow-ai-status">Analyzing your query...</div>
                        </div>

                        {/* RIGHT PIPE */}
                        <div className="scene-pipe pipe-r">
                            <div className="pipe-track"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                            <div className="pipe-dot"></div>
                        </div>

                        {/* KNOWLEDGE BASE PANEL */}
                        <div className="scene-actor">
                            <div className="flow-kb-card">
                                <div className="kb-header">
                                    <div className="kb-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="kb-title">Knowledge Base</div>
                                        <div className="kb-sub">Your Business Information</div>
                                    </div>
                                </div>

                                <div className="flow-kb-rows">
                                    <div className="flow-kb-row kb-match">
                                        <span className="kb-row-left">
                                            <span className="kb-row-icon">&#128336;</span>
                                            <span>Timings &amp; Hours</span>
                                        </span>
                                        <span className="kb-row-arrow">&rsaquo;</span>
                                    </div>
                                    <div className="flow-kb-row">
                                        <span className="kb-row-left">
                                            <span className="kb-row-icon">&#127991;&#65039;</span>
                                            <span>Pricing &amp; Services</span>
                                        </span>
                                        <span className="kb-row-arrow">&rsaquo;</span>
                                    </div>
                                    <div className="flow-kb-row">
                                        <span className="kb-row-left">
                                            <span className="kb-row-icon">&#128205;</span>
                                            <span>Delivery Areas</span>
                                        </span>
                                        <span className="kb-row-arrow">&rsaquo;</span>
                                    </div>
                                    <div className="flow-kb-row">
                                        <span className="kb-row-left">
                                            <span className="kb-row-icon">&#128222;</span>
                                            <span>Contact &amp; FAQs</span>
                                        </span>
                                        <span className="kb-row-arrow">&rsaquo;</span>
                                    </div>
                                </div>

                                <div className="flow-kb-badge">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        <path d="M9 12l2 2 4-4" />
                                    </svg>
                                    <span>Business Knowledge Base</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* HANDWRITTEN ANNOTATION ON RIGHT */}
                    <div className="flow-annotation">
                        <div className="annotation-text">
                            <span>Smart</span>
                            <span>Replies</span>
                            <span>Real Impact</span>
                        </div>
                        <svg width="34" height="42" viewBox="0 0 34 42" fill="none" className="annotation-arrow">
                            <path d="M22 2 C28 14, 26 26, 6 34" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" fill="none" />
                            <path d="M4 27 L6 34 L13 36" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                    </div>
                </div>

                {/* BOTTOM EXAMPLE CONVERSATIONS SECTION */}
                <div className="convos-header-row fade-up">
                    <div className="convos-header-left">
                        <div className="convos-title-wrap">
                            <span className="convos-sparkle">&#10022;</span>
                            <h3 className="convos-title">Example Conversations</h3>
                        </div>
                        <p className="convos-sub">Real questions. Accurate answers. Automatically.</p>
                    </div>
                    <a href="#demo" className="convos-view-more">
                        View more <span>&rarr;</span>
                    </a>
                </div>

                <div className="convos-cards-grid fade-up delay-2">
                    {/* Card 1: Priya Sharma */}
                    <div className="convo-card">
                        <div className="convo-card-top">
                            <div className="convo-user-meta">
                                <div className="convo-avatar convo-avatar-blue">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#2563EB">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="convo-user-name">Priya Sharma</div>
                                    <div className="convo-user-status"><span className="online-dot">&#9679;</span> Online</div>
                                </div>
                            </div>
                            <div className="convo-tag tag-blue">
                                <span>&#128336;</span> Timings
                            </div>
                        </div>

                        <div className="convo-chat-stack">
                            <div className="convo-user-msg-row">
                                <div className="convo-mini-avatar">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#94A3B8">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                                <div className="convo-user-bubble">
                                    <span>What are your timings?</span>
                                    <span className="msg-time">11:24 AM</span>
                                </div>
                            </div>

                            <div className="convo-ai-reply-box">
                                <div className="convo-ai-mascot">
                                    <DocLiftRobotMascot size={26} />
                                </div>
                                <div className="convo-ai-content">
                                    <p className="ai-text">
                                        We're open <strong>10 AM &ndash; 8 PM</strong> (Mon&ndash;Sat). Sundays <strong>11 AM &ndash; 6 PM</strong>. Anything else I can help with?
                                    </p>
                                    <div className="ai-meta">
                                        <span className="ai-time">11:25 AM</span>
                                        <DoubleCheckIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Rahul Verma */}
                    <div className="convo-card">
                        <div className="convo-card-top">
                            <div className="convo-user-meta">
                                <div className="convo-avatar convo-avatar-purple">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#9333EA">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="convo-user-name">Rahul Verma</div>
                                    <div className="convo-user-status"><span className="online-dot">&#9679;</span> Online</div>
                                </div>
                            </div>
                            <div className="convo-tag tag-purple">
                                <span>&#128197;</span> Appointments
                            </div>
                        </div>

                        <div className="convo-chat-stack">
                            <div className="convo-user-msg-row">
                                <div className="convo-mini-avatar">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#94A3B8">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                                <div className="convo-user-bubble">
                                    <span>Can I book an appointment for Saturday?</span>
                                    <span className="msg-time">11:32 AM</span>
                                </div>
                            </div>

                            <div className="convo-ai-reply-box">
                                <div className="convo-ai-mascot">
                                    <DocLiftRobotMascot size={26} />
                                </div>
                                <div className="convo-ai-content">
                                    <p className="ai-text">
                                        Absolutely! Slots <strong>available at 11 AM, 2 PM and 5 PM</strong> on Saturday. Which works for you?
                                    </p>
                                    <div className="ai-meta">
                                        <span className="ai-time">11:33 AM</span>
                                        <DoubleCheckIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card 3: Anita Mehta */}
                    <div className="convo-card">
                        <div className="convo-card-top">
                            <div className="convo-user-meta">
                                <div className="convo-avatar convo-avatar-green">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#16A34A">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="convo-user-name">Anita Mehta</div>
                                    <div className="convo-user-status"><span className="online-dot">&#9679;</span> Online</div>
                                </div>
                            </div>
                            <div className="convo-tag tag-green">
                                <span>&#127991;&#65039;</span> Pricing
                            </div>
                        </div>

                        <div className="convo-chat-stack">
                            <div className="convo-user-msg-row">
                                <div className="convo-mini-avatar">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#94A3B8">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                                <div className="convo-user-bubble">
                                    <span>What's the price for a haircut?</span>
                                    <span className="msg-time">11:45 AM</span>
                                </div>
                            </div>

                            <div className="convo-ai-reply-box">
                                <div className="convo-ai-mascot">
                                    <DocLiftRobotMascot size={26} />
                                </div>
                                <div className="convo-ai-content">
                                    <p className="ai-text">
                                        Haircuts start at <strong>&#8377;299</strong>. We also have <strong>combo</strong> packages. Want me to share the <strong>full menu</strong>?
                                    </p>
                                    <div className="ai-meta">
                                        <span className="ai-time">11:46 AM</span>
                                        <DoubleCheckIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BENEFITS / WHY DOCLIFT */}
            <section id="benefits" className="unified-split-section bg-subtle-blue">
                <div className="unified-split-container">
                    {/* Left Column */}
                    <div className="split-left-col fade-up">
                        <span className="split-pill-badge">WHY DOCLIFT</span>
                        <h2 className="split-title">
                            Everything you need.<br />
                            <span className="highlight-blue">Nothing extra.</span>
                        </h2>
                        <p className="split-desc">
                            Simple tools built around how small businesses actually work.
                        </p>
                        <div className="split-checks-row">
                            <div className="split-check-item">
                                <span className="split-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Save time</span>
                            </div>
                            <div className="split-check-item">
                                <span className="split-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Work smarter</span>
                            </div>
                            <div className="split-check-item">
                                <span className="split-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Stay organized</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="split-right-col fade-up delay-1">
                        <div className="benefits-combo-row">
                            {/* Trio of Feature Cards */}
                            <div className="feature-cards-trio">
                                {/* Card 1 */}
                                <div className="feature-box-card">
                                    <div className="feature-icon-badge badge-blue">
                                        <ChatBubbleIcon />
                                    </div>
                                    <h3 className="feature-card-title">Talk to customers</h3>
                                    <p className="feature-card-desc">
                                        Manage all your customer conversations from one simple place.
                                    </p>
                                    <a href="#flow" className="feature-learn-btn">
                                        <span className="learn-arrow-circle">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </span>
                                        <span>Learn more</span>
                                    </a>
                                </div>

                                {/* Card 2 */}
                                <div className="feature-box-card">
                                    <span className="feature-ai-pill">AI Powered</span>
                                    <div className="feature-icon-badge badge-green">
                                        <BotHeadIcon />
                                    </div>
                                    <h3 className="feature-card-title">Let AI answer</h3>
                                    <p className="feature-card-desc">
                                        Automatically reply to common questions &mdash; timings, pricing, delivery, availability.
                                    </p>
                                    <a href="#flow" className="feature-learn-btn">
                                        <span className="learn-arrow-circle">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </span>
                                        <span>Learn more</span>
                                    </a>
                                </div>

                                {/* Card 3 */}
                                <div className="feature-box-card">
                                    <div className="feature-icon-badge badge-purple">
                                        <ChartTrendIcon />
                                    </div>
                                    <h3 className="feature-card-title">Never miss a customer</h3>
                                    <p className="feature-card-desc">
                                        Respond instantly, even when you're busy, closed, or unavailable.
                                    </p>
                                    <a href="#flow" className="feature-learn-btn">
                                        <span className="learn-arrow-circle">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </span>
                                        <span>Learn more</span>
                                    </a>
                                </div>
                            </div>

                            {/* Floating Interactive Phone Mockup & Doodles */}
                            <div className="floating-mockup-wrapper">
                                <div className="floating-phone-aura"></div>

                                {/* Top-Right Doodle */}
                                <div className="doodle-item doodle-benefits-top">
                                    <div className="doodle-text">Smarter<br />Replies</div>
                                    <svg width="34" height="34" viewBox="0 0 40 40" fill="none" style={{ marginTop: 2, marginRight: 8 }}>
                                        <path d="M28 4 C22 14, 18 22, 10 28" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3" />
                                        <path d="M8 22 L10 28 L16 26" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                {/* Phone Card Mockup */}
                                <div className="floating-phone-card">
                                    <div className="phone-header">
                                        <div className="phone-avatar">
                                            <DocLiftRobotMascot size={20} />
                                        </div>
                                        <div>
                                            <div className="phone-bot-name">DocLift AI</div>
                                            <div className="phone-bot-status">
                                                <span style={{ fontSize: "8px" }}>●</span> Online
                                            </div>
                                        </div>
                                    </div>

                                    <div className="phone-msg-bubble">
                                        Hi! How can I help you today?
                                    </div>

                                    <div className="phone-typing-bubble">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className="phone-input-bar">
                                        <span className="phone-input-placeholder">Type message...</span>
                                        <div className="phone-send-btn">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom-Left Doodle */}
                                <div className="doodle-item doodle-benefits-bottom">
                                    <div className="doodle-text">Happier<br />Customers</div>
                                    <svg width="38" height="24" viewBox="0 0 50 30" fill="none" style={{ marginTop: 2, marginLeft: 14 }}>
                                        <path d="M4 22 C16 26, 32 20, 42 6" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3" />
                                        <path d="M34 6 L42 6 L42 14" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SETUP / HOW IT WORKS */}
            <section id="howitworks" className="unified-split-section bg-subtle-gradient">
                <div className="unified-split-container">
                    {/* Left Column */}
                    <div className="split-left-col fade-up">
                        <span className="split-pill-badge">SETUP</span>
                        <h2 className="split-title">
                            Up and running<br />
                            <span className="highlight-blue">in minutes</span>
                        </h2>
                        <p className="split-desc">
                            Three simple steps &mdash; no technical knowledge required.
                        </p>
                    </div>

                    {/* Right Column: 3 Steps Timeline with Animated Connectors */}
                    <div className="split-right-col fade-up delay-1">
                        <div className="setup-timeline-row">
                            {/* Step 1 */}
                            <div className="setup-step-box">
                                <div className="setup-step-icon-outer step-icon-green">
                                    <div style={{ width: 34, height: 34, color: "#16A34A" }}>
                                        <WhatsAppIcon />
                                    </div>
                                    <div className="setup-step-num-badge">1</div>
                                </div>
                                <h3 className="setup-step-title">Connect WhatsApp</h3>
                                <p className="setup-step-desc">
                                    Link your existing business WhatsApp number in one click.
                                </p>
                            </div>

                            {/* Connector 1 */}
                            <div className="setup-connector-line">
                                <div className="setup-connector-pulse"></div>
                                <svg className="setup-connector-arrowhead" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>

                            {/* Step 2 */}
                            <div className="setup-step-box">
                                <div className="setup-step-icon-outer step-icon-purple">
                                    <DocEditIcon />
                                    <div className="setup-step-num-badge">2</div>
                                </div>
                                <h3 className="setup-step-title">Add your info</h3>
                                <p className="setup-step-desc">
                                    Products, services, FAQs, pricing &mdash; just paste in plain language.
                                </p>
                            </div>

                            {/* Connector 2 */}
                            <div className="setup-connector-line">
                                <div className="setup-connector-pulse delay-pulse"></div>
                                <svg className="setup-connector-arrowhead" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>

                            {/* Step 3 */}
                            <div className="setup-step-box">
                                <div className="setup-step-icon-outer step-icon-blue">
                                    <ChatTripleIcon />
                                    <div className="setup-step-num-badge">3</div>
                                </div>
                                <h3 className="setup-step-title">Start chatting</h3>
                                <p className="setup-step-desc">
                                    DocLift answers customers automatically using your information.
                                </p>
                            </div>

                            {/* Doodle on Right */}
                            <div className="doodle-item doodle-setup-right">
                                <div className="doodle-text">From setup<br />to success</div>
                                <svg width="34" height="34" viewBox="0 0 40 40" fill="none" style={{ marginTop: 2, marginRight: 6 }}>
                                    <path d="M26 4 C22 14, 18 22, 10 28" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3" />
                                    <path d="M8 22 L10 28 L16 26" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section id="for-whom" className="unified-split-section">
                <div className="unified-split-container">
                    {/* Left Column */}
                    <div className="split-left-col fade-up">
                        <span className="split-pill-badge">WHO IT'S FOR</span>
                        <h2 className="split-title">
                            Made for small<br />
                            <span className="highlight-blue">businesses</span>
                        </h2>
                        <p className="split-desc">
                            If you talk to customers on WhatsApp, DocLift is for you.
                        </p>
                        <a href="#flow" className="split-cta-btn">
                            <span>Explore all solutions</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </a>
                    </div>

                    {/* Right Column: 10 Industries Grid */}
                    <div className="split-right-col fade-up delay-1">
                        <div className="industry-grid-wrapper">
                            <div className="industries-cards-grid">
                                {/* 1. Retailers */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#FFE4E6" }}>
                                        <ShoppingBagIcon />
                                    </div>
                                    <span className="industry-name">Retailers</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 2. Clinics */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#CCFBF1" }}>
                                        <ClinicCrossIcon />
                                    </div>
                                    <span className="industry-name">Clinics</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 3. Salons */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#F3E8FF" }}>
                                        <ScissorsIcon />
                                    </div>
                                    <span className="industry-name">Salons</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 4. Restaurants */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#FFEDD5" }}>
                                        <UtensilsIcon />
                                    </div>
                                    <span className="industry-name">Restaurants</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 5. Service Businesses */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#DBEAFE" }}>
                                        <CogIcon />
                                    </div>
                                    <span className="industry-name">Service Businesses</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 6. Professionals */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#EDE9FE" }}>
                                        <UserProfileIcon />
                                    </div>
                                    <span className="industry-name">Professionals</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 7. Local Delivery */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#D1FAE5" }}>
                                        <DeliveryTruckIcon />
                                    </div>
                                    <span className="industry-name">Local Delivery</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 8. Beauty & Wellness */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#FCE7F3" }}>
                                        <LotusFlowerIcon />
                                    </div>
                                    <span className="industry-name">Beauty &amp; Wellness</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 9. Tutors & Coaches */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#E0E7FF" }}>
                                        <GraduationCapIcon />
                                    </div>
                                    <span className="industry-name">Tutors &amp; Coaches</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>

                                {/* 10. Contractors */}
                                <div className="industry-pill-card">
                                    <div className="industry-icon-box" style={{ background: "#FEF3C7" }}>
                                        <HardHatIcon />
                                    </div>
                                    <span className="industry-name">Contractors</span>
                                    <span className="industry-chevron"><ChevronRightIcon /></span>
                                </div>
                            </div>

                            {/* Doodle on Right */}
                            <div className="doodle-item doodle-industry-right">
                                <div className="doodle-text">Built for<br />every business</div>
                                <svg width="34" height="34" viewBox="0 0 40 40" fill="none" style={{ marginTop: 2, marginRight: 6 }}>
                                    <path d="M26 4 C22 14, 18 22, 10 28" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3" />
                                    <path d="M8 22 L10 28 L16 26" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* AI POSITIONING / WHY DOCLIFT */}
            <section id="ai-section">
                <div className="ai-section-container">
                    {/* Left Column */}
                    <div className="ai-left-col fade-up">
                        <span className="split-pill-badge">WHY DOCLIFT</span>
                        <h2 className="split-title">
                            Your business.<br />
                            Your knowledge. Your AI.
                        </h2>
                        <p className="split-desc">
                            DocLift answers customers using only the information you provide. No guesswork. No surprises. Just your business, available 24/7.
                        </p>
                        <div className="ai-checklist-col">
                            <div className="ai-check-item">
                                <span className="ai-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Accurate responses</span>
                            </div>
                            <div className="ai-check-item">
                                <span className="ai-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Your business, your control</span>
                            </div>
                            <div className="ai-check-item">
                                <span className="ai-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Works 24/7</span>
                            </div>
                        </div>
                    </div>

                    {/* Center Graphic: Floating Phone + Document Sheet + Floating Purple Robot */}
                    <div className="ai-center-graphic fade-up delay-1">
                        <div className="ai-center-blob"></div>

                        {/* Document sheet background */}
                        <div className="ai-bg-doc-sheet">
                            <div className="doc-sheet-line w-80"></div>
                            <div className="doc-sheet-line w-60"></div>
                            <div className="doc-sheet-line w-90"></div>
                            <div className="doc-sheet-line w-40"></div>
                            <div className="doc-sheet-line w-80" style={{ marginTop: 8 }}></div>
                            <div className="doc-sheet-line w-60"></div>
                        </div>

                        {/* Floating Purple Robot Face Badge */}
                        <div className="ai-floating-purple-robot">
                            <div className="purple-bot-sparks">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            <div className="purple-bot-badge">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="10" rx="3" fill="none" />
                                    <circle cx="8.5" cy="16" r="1.5" fill="#FFFFFF" />
                                    <circle cx="15.5" cy="16" r="1.5" fill="#FFFFFF" />
                                    <path d="M12 2v4" />
                                    <circle cx="12" cy="2" r="1.5" fill="#FFFFFF" />
                                    <path d="M2 15h1" />
                                    <path d="M21 15h1" />
                                    <path d="M9 19c1 .8 2 .8 3 0" />
                                </svg>
                            </div>
                        </div>

                        {/* Floating Chat Mockup */}
                        <div className="ai-chat-mockup-card">
                            <div className="phone-header">
                                <div className="phone-avatar" style={{ background: "#DCFCE7" }}>
                                    <DocLiftRobotMascot size={20} />
                                </div>
                                <div>
                                    <div className="phone-bot-name">DocLift AI</div>
                                    <div className="phone-bot-status">
                                        <span style={{ fontSize: "8px" }}>●</span> Online
                                    </div>
                                </div>
                            </div>

                            <div className="phone-msg-bubble">
                                Hi! How can I help you today?
                            </div>

                            <div className="phone-typing-bubble">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>

                            <div className="phone-input-bar">
                                <span className="phone-input-placeholder">Type message...</span>
                                <div className="phone-send-btn" style={{ background: "#065F46" }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: 3 Horizontal Cards Stacked */}
                    <div className="ai-right-stack fade-up delay-2">
                        {/* Card 1 */}
                        <div className="ai-stack-card active-blue">
                            <div className="ai-stack-icon-box" style={{ background: "#EFF6FF" }}>
                                <ChatBubbleIcon />
                            </div>
                            <div className="ai-stack-content">
                                <div className="ai-stack-title">Talk to customers</div>
                                <div className="ai-stack-desc">
                                    Manage all your customer conversations from one simple place.
                                </div>
                            </div>
                            <span className="ai-stack-chevron"><ChevronRightIcon /></span>
                        </div>

                        {/* Card 2 */}
                        <div className="ai-stack-card">
                            <div className="ai-stack-icon-box" style={{ background: "#ECFDF5" }}>
                                <BotHeadIcon />
                            </div>
                            <div className="ai-stack-content">
                                <div className="ai-stack-title-row">
                                    <span className="ai-stack-title">Let AI answer</span>
                                    <span className="feature-ai-pill" style={{ position: "static", padding: "2px 8px" }}>AI Powered</span>
                                </div>
                                <div className="ai-stack-desc">
                                    Automatically reply to common questions &mdash; timings, pricing, delivery, availability.
                                </div>
                            </div>
                            <span className="ai-stack-chevron"><ChevronRightIcon /></span>
                        </div>

                        {/* Card 3 */}
                        <div className="ai-stack-card">
                            <div className="ai-stack-icon-box" style={{ background: "#EEF2FF" }}>
                                <ChartTrendIcon />
                            </div>
                            <div className="ai-stack-content">
                                <div className="ai-stack-title">Never miss a customer</div>
                                <div className="ai-stack-desc">
                                    Respond instantly, even when you're busy, closed, or unavailable.
                                </div>
                            </div>
                            <span className="ai-stack-chevron"><ChevronRightIcon /></span>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing">
                {/* Section Header */}
                <div className="pricing-header-wrap fade-up">
                    <span className="pricing-badge-green">PRICING</span>
                    <h2 className="pricing-main-title">Simple, honest pricing.</h2>
                    <p className="pricing-main-sub">No surprises. No hidden charges. Ever.</p>
                </div>

                {/* Pricing Cards */}
                <div className="pricing-cards-two fade-up delay-1">
                    {/* Card 1: Emerald First Year FREE */}
                    <div className="price-card-emerald">
                        {/* Sparkles */}
                        <span className="emerald-sparkle sparkle-pos-1">✦</span>
                        <span className="emerald-sparkle sparkle-pos-2">✦</span>
                        <span className="emerald-sparkle sparkle-pos-3">✦</span>
                        <span className="emerald-sparkle sparkle-pos-4">✦</span>

                        <div className="emerald-offer-pill">
                            <span>🎉</span> LIMITED TIME OFFER
                        </div>
                        <div className="emerald-plan-name">First Year</div>
                        <div className="emerald-big-amount">FREE</div>
                        <p className="emerald-plan-note">
                            Completely free for your entire first year.<br />
                            No card. No catch. No conditions.
                        </p>
                        <ul className="emerald-features-list">
                            <li>
                                <span className="emerald-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>No credit card required</span>
                            </li>
                            <li>
                                <span className="emerald-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>No setup fee</span>
                            </li>
                            <li>
                                <span className="emerald-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>No monthly charges</span>
                            </li>
                            <li>
                                <span className="emerald-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>No hidden fees</span>
                            </li>
                            <li>
                                <span className="emerald-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Full product access, all features</span>
                            </li>
                            <li>
                                <span className="emerald-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Unlimited customer conversations</span>
                            </li>
                        </ul>
                    </div>

                    {/* Card 2: White Annual Plan */}
                    <div className="price-card-white">
                        <div className="white-plan-badge">AFTER YEAR 1</div>
                        <div className="white-plan-name">Annual Plan</div>
                        <div className="white-amount-wrap">
                            <span className="white-big-amount">₹12,000</span>
                            <span className="white-amount-period">/yr</span>
                        </div>
                        <p className="white-plan-note">
                            Billed once a year. That's just ₹1,000/month.
                        </p>
                        <ul className="white-features-list">
                            <li>
                                <span className="white-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Everything from year 1</span>
                            </li>
                            <li>
                                <span className="white-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Flat price &mdash; no tiers</span>
                            </li>
                            <li>
                                <span className="white-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Cancel any time</span>
                            </li>
                            <li>
                                <span className="white-check-circle">
                                    <CheckCircleIcon />
                                </span>
                                <span>Priority support</span>
                            </li>
                        </ul>

                        <Link to="/dashboard" className="white-card-cta-btn">
                            <span>Get Started</span>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </Link>
                    </div>
                </div>

                {/* Bottom Zero Fees Pill Bar */}
                <div className="pricing-bottom-bar-wrap fade-up delay-3">
                    <div className="pricing-zero-fees-label">Zero fees in your first year</div>
                    <div className="pricing-zero-fees-pill-bar">
                        <div className="zero-fee-badge-item">
                            <span className="zero-fee-icon-circle">💳</span>
                            <span>No credit card</span>
                        </div>
                        <div className="zero-fee-badge-item">
                            <span className="zero-fee-icon-circle">⚙️</span>
                            <span>No setup fee</span>
                        </div>
                        <div className="zero-fee-badge-item">
                            <span className="zero-fee-icon-circle">🔒</span>
                            <span>No lock-in</span>
                        </div>
                        <div className="zero-fee-badge-item">
                            <span className="zero-fee-icon-circle">🆓</span>
                            <span>No auto-charge</span>
                        </div>
                        <div className="zero-fee-badge-item">
                            <span className="zero-fee-icon-circle">✨</span>
                            <span>No hidden cost</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section id="final-cta">
                <h2 className="fade-up">Ready to make WhatsApp work for your business?</h2>
                <p className="fade-up delay-1">
                    Join hundreds of small businesses already saving time every week.
                </p>
                <Link to="/dashboard" className="btn-cta-white fade-up delay-2" id="final-get-started">
                    <WhatsAppIcon />
                    Get Started — Free
                </Link>
            </section>

            {/* FOOTER */}
            <footer>
                <span className="brand">DocLift</span>
                <span className="tagline">
                    WhatsApp AI for small businesses &nbsp;&middot;&nbsp; Built in India &#127470;&#127475;
                </span>
            </footer>
        </div>
    );
};

export default HomeComponent2;
